import './EditDetails.scss';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { loadAppointmentsRequests } from 'actions/appointments';
import { loadClientList, patchAnimal } from 'actions/clients';
import Button from 'atoms/button';
import { DateInput } from 'atoms/date-input';
import Dropdown from 'atoms/dropdown';
import Input from 'atoms/input';
import toast from 'atoms/toast';
import useQuery from 'hooks/useQuery';
import { refreshAppointments } from 'organisms/Appointments/AppointmentActions/util';
import {
  closeDrawer,
  requestFulfilled,
  requestOngoing,
} from 'store/reducers/ui';
import { generateKey, prettifyPhoneNumber } from 'util';

const SHOW_OWNER_DETAILS = false;

export default function EditDetailsDrawer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const querySearch = useQuery();

  const { core } = useSelector((state) => state.main);
  const { animal, user, page, query } =
    useSelector((state) => state.ui.drawer.data) || {};
  const [vaccinationList, setVaccinationList] = useState([]);
  const vaccinationForAnimalType = core.vaccination.filter(
    (el) => el.animalTypeId === animal?.type?.id
  );
  const vaccinationIdsList = vaccinationList
    .map((vaccination) =>
      vaccination?.id && vaccination?.value ? vaccination.id : null
    )
    .filter((vaccination) => !!vaccination);

  const [breed, setBreed] = useState(null);
  const [size, setSize] = useState(null);
  const [hairLength, setHairLength] = useState(null);

  useEffect(() => {
    setBreed(animal?.breed);
    setSize(animal?.size);
    setHairLength(animal?.hairLength);
    setVaccinationList(animal?.vaccinations);
  }, [animal?.breed, animal?.size, animal?.hairLength, animal?.vaccinations]);

  const onAddVaccine = () => {
    setVaccinationList([...vaccinationList, {}]);
  };

  const onCancel = () => {
    dispatch(closeDrawer());
  };

  const onSave = () => {
    if (animal?.uuid) {
      dispatch(requestOngoing());
      dispatch(
        patchAnimal(animal?.uuid, {
          breed_id: breed?.id,
          size_id: size?.id,
          hair_length_id: hairLength?.id,
          vaccinations: vaccinationList,
        })
      ).then(({ success }) => {
        if (success) {
          toast({ text: 'Details edited!' });
          dispatch(loadAppointmentsRequests(animal?.uuid));
          refreshAppointments({
            location,
            query: querySearch,
            dispatch,
            auid: animal?.uuid,
          });
          if (page) dispatch(loadClientList({ page, query }));
        }
        dispatch(requestFulfilled());
        dispatch(closeDrawer());
      });
    }
  };

  return (
    <div className="edit-details-drawer flex-column">
      <div className="w-100 flex-column">
        <h2 className="f26 mb-24">Edit details</h2>

        {SHOW_OWNER_DETAILS && (
          <div className="edit-owner-details-panel flex-column mb-12 p-24 border-light-purple radius-24">
            <h4>Owner details</h4>
            <Input
              id="userName"
              value={`${user?.firstName} ${user?.lastName}`}
              size="small"
              disabled
              className="w-100 mt-16"
            />
            {user?.phone ? (
              <Input
                id="phone"
                value={prettifyPhoneNumber({
                  number: user.phone.number,
                  countryCode: user.phone.country?.code.value,
                })}
                size="small"
                disabled
                className="w-100 mt-16"
              />
            ) : null}
          </div>
        )}

        <div className="edit-pet-details-panel flex-column mb-12 p-24 border-light-purple radius-24">
          <h4>Pet details</h4>
          <Input
            id="animalName"
            value={animal?.name}
            size="small"
            disabled
            className="w-100 mt-16"
          />
          <Dropdown
            placeholder="Breed"
            className="w-100 mt-16"
            items={core.breed.filter(
              (el) => el.animalTypeId === animal?.type?.id
            )}
            size="small"
            onClick={(value) => setBreed(value)}
            value={animal?.breed}
          />
          <Dropdown
            placeholder="Size"
            className="w-100 mt-16"
            items={core.size}
            size="small"
            onClick={(value) => setSize(value)}
            value={animal?.size}
          />
          <Dropdown
            placeholder="Hair length"
            className="w-100 mt-16"
            items={core.hair.length.filter(
              (el) => el.animalTypeId === animal?.type?.id
            )}
            size="small"
            onClick={(value) => setHairLength(value)}
            value={animal?.hairLength}
          />
        </div>

        <div className="update-vaccinations-panel flex-column mb-12 p-24 border-light-purple radius-24">
          <h4>Vaccinations</h4>
          {vaccinationList.map((vaccination, index) => (
            <div className="flex-row gap-16 mt-16" key={generateKey()}>
              <Dropdown
                placeholder="Vaccination"
                className="w-50"
                items={vaccinationForAnimalType.filter(
                  (vaccinationOption) =>
                    vaccinationOption?.id === vaccination?.id ||
                    !vaccinationIdsList.includes(vaccinationOption?.id)
                )}
                size="small"
                onClick={(value) => {
                  const updatedVaccination = {
                    ...vaccinationList[index],
                    ...value,
                  };
                  const vaccinationListCopy = vaccinationList.slice(0);
                  vaccinationListCopy[index] = updatedVaccination;
                  return setVaccinationList(vaccinationListCopy);
                }}
                value={
                  vaccination?.id && vaccination?.value ? vaccination : null
                }
              />
              <DateInput
                className="w-50"
                placeholder="Expiration date"
                onChange={(date) => {
                  const updatedVaccination = {
                    ...vaccinationList[index],
                    expirationDate: date,
                  };
                  const vaccinationListCopy = vaccinationList.slice(0);
                  vaccinationListCopy[index] = updatedVaccination;
                  return setVaccinationList(vaccinationListCopy);
                }}
                value={
                  vaccination?.expirationDate
                    ? vaccination.expirationDate
                    : null
                }
                size="small"
              />
            </div>
          ))}
          <div className="flex-row mt-16">
            <Button
              className="f14 fw-400"
              text="Add vaccine"
              size="small"
              variant="white-outlined"
              icon="add"
              iconPosition="start"
              onClick={onAddVaccine}
            />
          </div>
        </div>

        <div className="flex-row justify-end gap-8">
          <Button
            className="f14 fw-400"
            size="small"
            text="Cancel"
            variant="white-outlined"
            onClick={onCancel}
          />
          <Button
            className="f14 fw-400"
            size="small"
            text="Save changes"
            onClick={onSave}
          />
        </div>
      </div>
    </div>
  );
}
